import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { v4 as uuidv4 } from 'uuid';
import { COMPOSITION_DIMENSIONS, VIDEO_QUALITY } from '../../../constants';
import { CompositionConfig } from '../../types/CompositionConfig';
import { TrackType } from '../../types/Track/Type';
import { SegmentsTrack } from '../../types/Track/SegmentsTrack';
import { Track } from '../../types/Track/Track';
import {
  createCompositeFromOldComposite,
  createMainCompositeSegmentWithMetaData
} from './compositeSegment/compositeSegment';
import { getFontObject } from './compositeSegment/utils';
import { ElementsTrack } from '../../types/Track/ElementsTrack';
import { mapEffectsToPlayableTimeline } from '../animation/effectUtils';
import { mapScenesToPlayableTimeline } from '../animation/scenesUtils';
import { getPlayerConfigElements } from '../../ClipPlayerV2WithNewTimeline/utils/playerConfigGetters';
import { MAIN_TRACK_ID } from './constants';
import { SegmentType } from '../../types/Segment/Type';
import { updateVideoSegmentWithMetaData } from './videoSegment/videoSegmentWithMetadata';
import { CaptionTrackMetadata } from '../../types/Track/CaptionsTrack/CaptionsTrack';
import { canUseBrowserlessCaptions, currentClip } from '@/stores/clip';
import { Clip } from '@/domains/asset';
import { getClipFPS, isClipAudiogram, isFullLengthClip, isNotFullLengthClip } from '@/libs/clipUtils';
import { SceneChanges } from '@/domains/event';
import { SelectionElementType } from '@/App/remotion/components/Outlines/types';
import { buildCaptionMetadata, getCaptionPages } from '@/App/remotion/libs/caption/caption';
import { updatePlayerConfigWithDeletes } from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import {
  getAllCompositeSegments,
  getAllSegments
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/compositeUtils';
import { Page } from '@/App/remotion/types';

const getMaxBoundsForCompositeSegment = (clip: Clip) => {
  const fps = getClipFPS(clip);
  let maxBounds = {
    fromFrame: 0,
    toFrame: clip.asset_metadata.end * fps
  };

  if (isNotFullLengthClip(clip) && typeof clip.asset_metadata.maxVideoDuration === 'number') {
    maxBounds = {
      fromFrame: 0,
      toFrame: clip.asset_metadata.maxVideoDuration * fps
    };
  }

  if (
    typeof clip.asset_metadata.maxVideoDuration === 'number' &&
    clip.asset_metadata.maxVideoDuration > clip.asset_metadata.end &&
    isFullLengthClip(clip)
  ) {
    maxBounds = {
      fromFrame: 0,
      toFrame: clip.asset_metadata.maxVideoDuration * fps
    };
  }
  return maxBounds;
};

export const injectSegmentMetaDataInCompositeSegment = (clip: Clip) => {
  const newClip = structuredClone(clip);
  const playerConfig = newClip.asset_metadata.player_config;
  const allCompositeSegments = getAllCompositeSegments(newClip);

  const hasMoreThanOneCompositeSegment = allCompositeSegments.length > 1;

  const allCompositeSegmentHaveMetaData = allCompositeSegments.every(segment => segment.segment_metadata);

  if (!playerConfig) {
    return newClip;
  }

  if (hasMoreThanOneCompositeSegment && !allCompositeSegmentHaveMetaData) {
    throw new Error('Composite segments found but no metadata found. This should not happen.');
  }

  if (allCompositeSegmentHaveMetaData) {
    return newClip;
  }

  const allSegments = getAllSegments(newClip) || [];

  allSegments.forEach(segment => {
    if (segment.type === SegmentType.COMPOSITE) {
      segment.segment_metadata = {
        start: newClip.asset_metadata.start,
        end: newClip.asset_metadata.end,
        deletes: newClip.asset_metadata.deletes || {},
        edits: newClip.asset_metadata.edits || {},
        merges: newClip.asset_metadata.merges || [],
        excludes: newClip.asset_metadata.excludes || [],
        maxVideoDuration: clip.asset_metadata.maxVideoDuration || 0,
        captions: newClip.asset_metadata.captions || [],
        fitMode: newClip.asset_metadata.fit,
        scenes: newClip.asset_metadata.scenes || [],
        duration: newClip.asset_metadata.duration || 0
      };
    } else if (segment.type === SegmentType.VIDEO) {
      const maxBound = {
        fromFrame: segment.fromFrame,
        toFrame: segment.toFrame
      };
      const currentBound = {
        fromFrame: segment.fromFrame,
        toFrame: segment.toFrame
      };
      const fps = getClipFPS(newClip);
      segment.maxBounds = maxBound;
      segment.currentBounds = currentBound;
      segment.segment_metadata = {
        start: 0,
        end: (segment.toFrame - segment.fromFrame) / fps,
        deletes: {},
        maxVideoDuration: (segment.toFrame - segment.fromFrame) / fps,
        src: segment.video.details.src || ''
      };
    }
  });

  return newClip;
};

export const injectSegmentMetaDataInClip = (clip: Clip): Clip => {
  const allCompositeSegments = getAllCompositeSegments(clip);
  const hasMultipleCompositeSegments = allCompositeSegments.length > 1;
  if (hasMultipleCompositeSegments) {
    throw new Error('Multiple composite segments found. This should not happen.');
  }
  const compositeSegment = allCompositeSegments[0];
  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found. This should not happen.');
  }
  clip.asset_metadata = {
    ...clip.asset_metadata,
    deletes: compositeSegment.segment_metadata.deletes,
    edits: compositeSegment.segment_metadata.edits,
    merges: compositeSegment.segment_metadata.merges,
    excludes: compositeSegment.segment_metadata.excludes,
    captions: compositeSegment.segment_metadata.captions,
    fit: compositeSegment.segment_metadata.fitMode,
    scenes: compositeSegment.segment_metadata.scenes,
    duration: compositeSegment.segment_metadata.duration,
    maxVideoDuration: compositeSegment.segment_metadata.maxVideoDuration,
    start: compositeSegment.segment_metadata.start,
    end: compositeSegment.segment_metadata.end
  };
  return clip;
};

export const injectUpdatedMainTrackInPlayerConfig = (clip: Clip, mainTrack: SegmentsTrack): Clip => {
  if (!clip?.asset_metadata?.player_config || !clip.asset_metadata.player_config.playerConfig) {
    return clip;
  }

  // Replace the main (SEGMENTS) track in player config with the new mainTrack
  const updatedTracks = (clip.asset_metadata.player_config.playerConfig.tracks || []).map(track => {
    if (track.type === TrackType.SEGMENTS) {
      return mainTrack;
    }
    return track;
  });

  // If no main track was present, just append this main track
  const hasMainTrack = updatedTracks.some(track => track.type === TrackType.SEGMENTS);
  const finalTracks = hasMainTrack ? updatedTracks : [mainTrack, ...updatedTracks];

  clip.asset_metadata.player_config.playerConfig.tracks = finalTracks;
  return clip;
};

export function getPlayerConfigWithSplits({
  clip,
  sceneOverrides,
  shouldUpdatePlayerConfig,
  facialRecognitionToDefaultPosition
}: {
  clip: Clip;
  /**
   * This field is for creation of scenes from the event app. To be used only during initialization of the player config
   */
  sceneOverrides?: SceneChanges;
  shouldUpdatePlayerConfig?: boolean;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
}): CompositionConfig {
  if (!shouldUpdatePlayerConfig) {
    return clip.asset_metadata.player_config as CompositionConfig;
  }

  const videoQuality = VIDEO_QUALITY.FULL_HD;
  const height = COMPOSITION_DIMENSIONS[clip.asset_metadata.size][videoQuality].HEIGHT;
  const width = COMPOSITION_DIMENSIONS[clip.asset_metadata.size][videoQuality].WIDTH;

  const fps = getClipFPS(clip);

  const showCaptions = !!clip.asset_metadata.subtitle || isClipAudiogram(clip);

  const segmentsTrack: SegmentsTrack = {
    id: MAIN_TRACK_ID,
    name: 'Segments Track',
    type: TrackType.SEGMENTS,
    hideTrack: {
      inTimeline: false,
      inPlayer: false
    },
    segments: []
  };
  const existingSegmentsTrack = clip.asset_metadata.player_config?.playerConfig.tracks.find(
    track => track.type === TrackType.SEGMENTS
  ) as SegmentsTrack | undefined;
  segmentsTrack.segmentTransitions = existingSegmentsTrack?.segmentTransitions || [];
  const clipWithSegmentMetaData = injectSegmentMetaDataInCompositeSegment(clip);

  let durationInFrames = 0;
  const previousClip = currentClip.getSnapshot()[clip.id];

  if (clipWithSegmentMetaData.asset_metadata.player_config) {
    const allSegments = getAllSegments(clipWithSegmentMetaData);
    for (const segment of allSegments) {
      const isCompositeSegment = segment.type === SegmentType.COMPOSITE;
      if (isCompositeSegment) {
        const compositeSegmentDuration = (segment.segment_metadata?.duration || 0) * fps;
        const newCompositeSegment = createCompositeFromOldComposite({
          clip: clipWithSegmentMetaData,
          fps,
          duration: compositeSegmentDuration,
          offset: durationInFrames,
          sceneOverrides,
          facialRecognitionToDefaultPosition,
          oldCompositeSegment: segment
        });

        // Update effects if there are deletes
        if (segment.effects) {
          newCompositeSegment.effects = segment.effects;
        }

        // Compute playback-adjusted effects for the composite segment
        const playbackAdjustedEffects = mapEffectsToPlayableTimeline(
          newCompositeSegment.effects,
          newCompositeSegment.playRanges,
          fps
        );

        const playbackAdjustedScenes = mapScenesToPlayableTimeline(
          newCompositeSegment.scenes,
          newCompositeSegment.playRanges
        );

        const maxBounds = segment.maxBounds;

        segmentsTrack.segments.push({
          ...newCompositeSegment,
          playbackAdjustedEffects,
          playbackAdjustedScenes,
          maxBounds
        });
        durationInFrames += compositeSegmentDuration;
      } else {
        if (segment.type === SegmentType.VIDEO) {
          const newSegment = updateVideoSegmentWithMetaData(clip, segment, durationInFrames);
          durationInFrames += newSegment.toFrame - newSegment.fromFrame;
          segmentsTrack.segments.push(newSegment);
        } else {
          throw new Error('Type not implemented');
        }
      }
    }
  } else {
    const compositeSegmentDuration = (clip.asset_metadata?.duration || 0) * fps;
    const maxBounds = getMaxBoundsForCompositeSegment(clip);
    const compositeSegment = createMainCompositeSegmentWithMetaData({
      clip,
      fps,
      duration: compositeSegmentDuration,
      offset: durationInFrames,
      sceneOverrides,
      facialRecognitionToDefaultPosition
    });

    // Compute playback-adjusted effects for the composite segment
    const playbackAdjustedEffects = mapEffectsToPlayableTimeline(
      compositeSegment.effects,
      compositeSegment.playRanges,
      fps
    );

    const playbackAdjustedScenes = mapScenesToPlayableTimeline(compositeSegment.scenes, compositeSegment.playRanges);

    segmentsTrack.segments.push({
      ...compositeSegment,
      playbackAdjustedEffects,
      playbackAdjustedScenes,
      maxBounds
    });
    durationInFrames += compositeSegmentDuration;
  }

  injectUpdatedMainTrackInPlayerConfig(clipWithSegmentMetaData, segmentsTrack);

  let tracks: Track[] = [];
  if (segmentsTrack.segments.length) {
    const fontObject = getFontObject({ clip: clipWithSegmentMetaData, fontTarget: SelectionElementType.CAPTIONS });
    const existingElementsTracks = getPlayerConfigElements(clipWithSegmentMetaData?.asset_metadata?.player_config);

    // Update element tracks with deletes first
    let updatedElementsTracks: ElementsTrack[] = [];
    if (existingElementsTracks) {
      updatedElementsTracks = updatePlayerConfigWithDeletes(
        existingElementsTracks,
        fps,
        previousClip,
        clipWithSegmentMetaData
      );
    }
    let pages: Page[] = [];
    let metadata: CaptionTrackMetadata | undefined = undefined;
    const captionTrackOffset = 0;
    if (canUseBrowserlessCaptions()) {
      metadata = buildCaptionMetadata(clipWithSegmentMetaData, segmentsTrack);
    } else {
      pages = getCaptionPages({
        clip: clipWithSegmentMetaData,
        mainTrack: segmentsTrack,
        updatedElementTracks: updatedElementsTracks,
        captionTrackOffset: captionTrackOffset
      });
    }

    tracks.push({
      segmentId: 'random',
      name: 'Captions',
      id: uuidv4(),
      type: TrackType.CAPTIONS,
      hideTrack: {
        inPlayer: !showCaptions,
        inTimeline: true
      },
      pages,
      metadata,
      fromFrame: captionTrackOffset,
      toFrame: durationInFrames,
      ...fontObject
    });

    tracks.unshift(...updatedElementsTracks);
    tracks.unshift(segmentsTrack);
  } else {
    tracks = clip.asset_metadata.player_config?.playerConfig?.tracks || [];
  }

  return {
    height,
    width,
    fps,
    durationInFrames: Math.floor(durationInFrames),
    version: '0.0.1',
    playerConfig: {
      tracks
    }
  };
}
